/**
 * EduCore SPA paths CampusOne dashboards can open.
 *
 * These are routes in `educore_frontend` (`App.tsx` / `navigation.ts`), not API
 * prefixes. CampusOne only ever holds the product `launchUrl` (origin); appending a
 * path is how a KPI opens the right screen rather than the EduCore home page.
 *
 * Auth for a cold mid-path entry is EduCore's problem — CampusOne must not invent a
 * second SSO handshake. If the session is cold, EduCore's own auth already preserves
 * the deep link.
 */
export const EduCorePaths = {
  // Finance (bursar family)
  overview: '/dashboard/accountant-overview',
  fees: '/dashboard/fees',
  feeConfiguration: '/dashboard/fee-configuration',
  feeDuplicates: '/dashboard/fee-duplicates',
  feeWaivers: '/dashboard/fee-waivers',
  paymentApprovals: '/dashboard/payment-approvals',
  transactions: '/dashboard/accountant-transactions',
  ledger: '/dashboard/accountant-ledger',
  expenses: '/dashboard/accountant-expenses',
  reports: '/dashboard/accountant-reports',
  reconciliation: '/dashboard/accountant-reconciliation',
  financeAudit: '/dashboard/finance-audit',

  // Teaching
  teacherHome: '/dashboard/teacher-home',
  teacherAssignments: '/dashboard/teacher-assignments',
  teacherAttendance: '/dashboard/teacher-attendance',
  teacherTimetable: '/dashboard/teacher-timetable',
  grades: '/dashboard/grades',

  // Learner
  studentHome: '/dashboard/my-home',
  studentSchedule: '/dashboard/my-schedule',
  studentHomework: '/dashboard/my-homework',
  studentExams: '/dashboard/my-exams',
  studentAttendance: '/dashboard/my-attendance',
  studentProgress: '/dashboard/my-progress',
  studentFees: '/dashboard/my-fees',
  studentAcademicStructure: '/dashboard/my-academic-structure',
  studentCourseRegistration: '/dashboard/course-registration',
  /** Form B is issued from the course-registration screen once confirmed. */
  studentFormB: '/dashboard/course-registration',
  studentCaResults: '/dashboard/my-ca-results',
  studentExamResults: '/dashboard/my-exam-results',
  events: '/dashboard/events',

  // Parent / guardian
  parentDashboard: '/dashboard/parent-dashboard',
  parentFees: '/dashboard/parent-fees',
  parentReports: '/dashboard/parent-reports',
  parentTimetable: '/dashboard/parent-timetable',

  // Registry
  registrarOverview: '/dashboard/registrar-overview',
  admissions: '/dashboard/admissions',
  students: '/dashboard/students',
  enrollment: '/dashboard/enrollment',
  classes: '/dashboard/classes',

  // Examinations
  exams: '/dashboard/exams',

  // Library
  librarianOverview: '/dashboard/librarian-overview',
  library: '/dashboard/library',
  inventory: '/dashboard/inventory-management',

  // Transport
  transportOverview: '/dashboard/transport-manager-overview',
  transportManagement: '/dashboard/transport-management',

  // Leadership academic
  directorOverview: '/dashboard/director-overview',
  hodOverview: '/dashboard/hod-overview',
  attendance: '/dashboard/attendance',
} as const;

export type EduCorePath = (typeof EduCorePaths)[keyof typeof EduCorePaths];

/**
 * Join a product launch origin with an EduCore SPA path.
 *
 * Returns null when there is no launch URL — the caller must then fall back to
 * `/products` rather than open a broken absolute path on CampusOne's own host.
 */
export function buildEduCoreUrl(
  launchUrl: string | null | undefined,
  path: string,
): string | null {
  const base = launchUrl?.trim();
  if (!base) return null;

  try {
    const url = new URL(base);
    // launchUrl may already include a path (e.g. …/dashboard). Replace with the
    // target so we never produce …/dashboard/dashboard/fees.
    url.pathname = path.startsWith('/') ? path : `/${path}`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    // A misconfigured launch URL is a product-admin problem; do not open junk.
    return null;
  }
}

/** Bursar attention item id → EduCore screen that acts on it. */
export const ATTENTION_EDUCORE_PATH: Readonly<Record<string, EduCorePath>> = {
  OVERDUE_BEYOND_60: EduCorePaths.fees,
  UNPAID_BILLS: EduCorePaths.fees,
  FAILED_BILLS: EduCorePaths.transactions,
  PARTIAL_APPROACHING: EduCorePaths.fees,
  DUPLICATE_INVOICES: EduCorePaths.feeDuplicates,
  PENDING_APPROVALS: EduCorePaths.paymentApprovals,
  REFUNDS_AWAITING: EduCorePaths.transactions,
  UNDATED_BALANCES: EduCorePaths.feeConfiguration,
};
